<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple
            v-else
            borderless
            hover
            class="list"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
        >
            <b-thead>
                <b-tr>
                    <b-th>Item:</b-th>
                    <b-th>Patient:</b-th>
                    <b-th>Returned:</b-th>
                    <b-th>Restocked:</b-th>
                    <b-th>Total:</b-th>
                    <b-th>Reason:</b-th>
                    <b-th>Notes:</b-th>
                    <b-th>Issued:</b-th>
                    <b-th v-if="enableIntegratedPayments">Transaction ID:</b-th>
                    <b-th>Reference:</b-th>
                    <b-th>Date:</b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(r, i) in returns" :key="i">
                    <b-td>
                        <item-name :item="r.invoice_item.item" />
                    </b-td>
                    <b-td>
                        <patient-name
                            :use-router="USE_PATIENT_MODULE_VUE"
                            :patient="r.invoice_item.patient"
                        />
                    </b-td>
                    <b-td> {{ r.quantity }} {{ getUom(r.invoice_item) }} </b-td>
                    <b-td>
                        {{ r.restock }}
                        {{ getUom(r.invoice_item) }}</b-td
                    >
                    <b-td>
                        {{ _toCurrency(r.return_amount) }}
                    </b-td>
                    <b-td> {{ r.type.label }} </b-td>
                    <b-td> {{ r.note }} </b-td>
                    <b-td> {{ r.return.return_option.label }} </b-td>
                    <b-td v-if="enableIntegratedPayments">
                        <span
                            v-if="
                                (r.cc_transaction == 'STRIPE' ||
                                    r.cc_transaction == 'CCONNECT') &&
                                r.transaction_id
                            "
                        >
                            <transaction-id-link
                                v-for="transactionId in getTransactionIds(
                                    r.transaction_id
                                )"
                                :key="transactionId"
                                :transactionId="transactionId"
                                :transactionType="r.cc_transaction"
                                :account="refundAccount"
                            />
                        </span>
                        <span v-else>
                            -
                        </span>
                    </b-td>
                    <b-td>
                        <return-link
                            v-if="r.return.encoded_id"
                            :return="r.return"
                        />
                    </b-td>
                    <b-td>
                        {{ _carbonDateDisplay(r.return.post_date) }}
                    </b-td>
                </b-tr>
                <b-tr v-if="returns.length === 0">
                    <b-td :colspan="enableIntegratedPayments ? 11 : 10">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && meta.lastPage > 1"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getReturns"
        />
    </div>
</template>

<script>
/**
 * @name ReturnList
 * @description List of return for invoice page
 * @see apps/assets/js/module/billing/return.js
 * @see apps/application/views/staff/billing/invoice/open/sub/return/list.php
 */
import Loader from "@/components/elements/loader-list";
import InvoiceReturnService from "@/services/invoice/invoice-returns";
import PatientName from "@/components/commons/patient-name";
import ItemName from "@/components/commons/item-name";
import Payment from "@/services/payment";
import TransactionIdLink from "@/components/commons/transaction-id-link";
import EXTENSIONS from "@/constants/extensions";

export default {
    mixins: {},
    components: {
        Loader,
        PatientName,
        ItemName,
        TransactionIdLink,
        ReturnLink: () => import("@/components/commons/return-link")
    },
    props: {
        invoice: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            sortBy: "Return:",
            sortDesc: true,
            isLoading: false,
            returns: [],
            selectedReturn: {},
            selectedRefund: {},
            selectedClientname: null,
            meta: null,
            uoms: null,
            refundAccount: null,
            enableIntegratedPayments: false
        };
    },
    async mounted() {
        this.uoms = this.$store.getters["references/uoms"];
        this.getPaymentAccounts();
        this.getReturns();
    },
    methods: {
        getUom(invoiceItem) {
            if (!("item_uom" in invoiceItem)) return null;
            return invoiceItem.item_uom ? invoiceItem.item_uom.value : null;
        },
        getReturns(page = 1) {
            let self = this;
            self.returns = [];
            self.isLoading = true;
            const params = {
                page: page,
                perPage: self.PER_PAGE
            };
            InvoiceReturnService.getReturns(this.invoice.id, params)
                .then(res => {
                    if (res) {
                        self.returns = res.resources;
                        self.meta = res.meta;
                    } else {
                        throw "Error : Unable to retrieve returns list";
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to retrieve returns list",
                        "danger"
                    );
                    console.error(error);
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },
        isBusinessActorActive(client) {
            return (
                client &&
                client.single_business_actor &&
                client.single_business_actor.isactive
            );
        },
        getPaymentAccounts() {
            let self = this;

            let { CODE } = EXTENSIONS;
            self.enableIntegratedPayments =
                self._isExtensionEnabled(CODE.CODE_CCONNECT) ||
                self._isExtensionEnabled(CODE.CODE_GRAVITY) ||
                self._isExtensionEnabled(CODE.CODE_STRIPE);

            Payment.getPaymentAccounts(this._setting.business.id)
                .then(response => {
                    if (response.message.code == 200) {
                        self.refundAccount =
                            response.message.payment_processor_id;
                    } else {
                        self._message(response.message.description, "danger");
                        self.refundAccount = null;
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getTransactionIds(transactionIdString) {
            return transactionIdString.split(",");
        }
    }
};
</script>
